import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalDirective } from '@shared/directives/modal.directive';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IQuestionSet } from '@shared/interfaces/question-set.interface';
import { Job } from '@shared/models/job.model';
import { LoggerService } from '@shared/services/logger.service';
import { JobsService } from '@shared/services/jobs.service';
import { QuestionSetsService } from '@shared/services/question-sets.service';
import { ToastService } from '@shared/services/toast.service';
import { SessionStorage } from 'ngx-webstorage';
import { IJob } from '@shared/interfaces/job.interface';

@Component({
    selector: 'app-modals-edit-jobs',
    templateUrl: './modals.edit-jobs.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalsEditJobsComponent
  extends ModalDirective<IJob>
  implements OnInit
{
  @SessionStorage() private hiringFirm: IHiringFirm;

  public entry: FormGroup;
  public errors: any = [];

  public title = String('SETTINGS.JOBS.EDIT');
  public submitKey = String('BUTTONS.SUBMIT');

  public questionSets: { label: string; value: string }[] = [];

  private readonly constructorName: string = String(this.constructor.name);
  private allQuestionSets: IQuestionSet[];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _jobs: JobsService,
    private readonly _questionSets: QuestionSetsService,
    private readonly _toast: ToastService,
    private readonly _router: Router,
    private readonly _logger: LoggerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.createForm();
    this.getQuestionSets();

    this.openModal.subscribe((job: IJob) => {
      this.entry.patchValue({
        id: job.id,
        label: job.label,
        question_set_id: job.question_set?.id || ''
      });
    });
  }

  private getQuestionSets() {
    const filters = {
      page_size: 999,
      filter: {
        key: 'qs',
        value: 'scope:reference'
      }
    };

    const url = 'GET /question-sets';

    this._questionSets.get(filters).subscribe(
      (res: IQuestionSet[]) => {
        this._logger.info(this.constructorName, url, res);
        this.allQuestionSets = res;
        this.questionSets = this.allQuestionSets.map(qs => ({ label: qs.title, value: qs.id }));
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  public onDismiss() {
    this.entry.reset();
  }

  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      this.entry.disable();
      this.errors = [];

      if (value.id) {
        this.patch(value);
      } else {
        this._logger.error(this.constructorName, 'onSubmit', 'No job ID provided for edit');
        this.errors = ['No job ID provided for edit'];
        this.entry.enable();
      }
    }
  }

  public whenModalClose(type: string): void {
    if (type === 'SUBMIT') {
      this.errors = [];
      this.entityForm.ngSubmit.emit();
    }
  }

  protected createForm() {
    this.entry = this._fb.group({
      id: [''],
      label: ['', [Validators.required, Validators.maxLength(255)]],
      question_set_id: ['']
    });
  }

  private patch(value: any) {
    const selectedQuestionSet = value.question_set_id
      ? this.allQuestionSets.find(qs => qs.id === value.question_set_id)
      : null;

    const job = new Job({
      id: value.id,
      label: value.label,
      question_set: selectedQuestionSet
    });

    const jobData = job.apiData;

    // Remove question_set from relationships if it's null/undefined
    if (!selectedQuestionSet) {
      delete jobData.data.relationships.question_set;
    }

    // Remove hiring_firm if it's undefined
    if (!jobData.data.relationships.hiring_firm) {
      delete jobData.data.relationships.hiring_firm;
    }

    // If relationships object is empty, remove it entirely
    if (Object.keys(jobData.data.relationships).length === 0) {
      delete jobData.data.relationships;
    }

    const url = `PATCH /jobs/${jobData.data.id}`;

    this._jobs.patch(jobData).subscribe(
      (res: IJob) => {
        this._logger.info(this.constructorName, url, res);
        this.entry.enable();
        this.resetModal(res);
        this._toast.success('TOASTS.JOB_UPDATED');
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        this.errors = err;
        this.entry.enable();
      }
    );
  }
}
